import React, { useReducer, useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { CATEGORIES } from '../../common/categories';
import { fieldsReducer, initFieldsState, mapToSettings } from './protectedDomainsReducer';
import * as actions from './protectedDomainsActions';
import { useTabs } from '../../hooks/useTabs';
import { useSaveSettings } from '../../hooks/useSaveSettings';
import useSearch from '../../hooks/useSearch';
import { filterFieldsBySystemMode, getNewDomainsFromFile } from '../../common/utils';
import { downloadFile, httpPost, isUserPermitted } from '../../../utils';

import ProtectedDomain from '../../components/ProtectedDomain/ProtectedDomain';
import Button from '../../components/Controls/Button/Button';
import Category from '../../components/Category/Category';
import ImportFromFile from '../../../../components/Controls/ImportFromFile';
import { COMMON_FIELDS } from '../../common/fields';
import { ROLES, USER_ENGAGEMENT_PAGE_KEYS } from '@silverfort-nodelibs/common/utils/consts';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import _ from 'lodash';

const { LDAP_IP_OPTIONS } = COMMON_FIELDS;
const { PROTECTED_DOMAINS } = CATEGORIES;

const ProtectedDomains = ({ saveFunc, disableSave, systemMode }) => {
    const history = useHistory();
    const user = useSelector((state) => state.userData.user);
    const isEditor = isUserPermitted(user, 'settings', ROLES.EDITOR.value);

    const [selectNewDomainTab, setSelectNewDomainTab] = useState(false);
    const [showUploadDomainsModal, setShowUploadDomainsModal] = useState(false);

    const settings = useSelector((state) => state.settings.settings);
    const searchQuery = useSelector((state) => state.settings.searchQuery);

    const [fields, dispatch] = useReducer(fieldsReducer, { settings }, initFieldsState);
    const { ldapIpOptions, backupLdapIpOptions, ...domainFields } = fields;
    const visibleFields = useSearch(domainFields);

    const [selectedTab, setSelectedTab] = useTabs(PROTECTED_DOMAINS, visibleFields);
    const [save, discard] = useSaveSettings(saveFunc, settings, mapToSettings, fields, selectedTab, dispatch);

    const newDomainIds = Object.keys(visibleFields).filter(
        (fieldId) => !Object.keys(settings.DomainsConfigurations || {}).includes(fieldId)
    );

    useEffect(() => {
        httpPost('/data/userEngagement', { pageId: USER_ENGAGEMENT_PAGE_KEYS.SETTINGS_DOMAINS });
    }, []);

    useEffect(() => {
        if (newDomainIds.length > 0 && selectNewDomainTab) {
            history.push(`/Settings/${PROTECTED_DOMAINS.NAME}#${newDomainIds[0]}`);
            setSelectedTab(newDomainIds[0]);
            setSelectNewDomainTab(false);
        }
    }, [selectNewDomainTab, newDomainIds, history, setSelectedTab]);

    const addDomain = useCallback(() => {
        dispatch({
            type: actions.ADD_DOMAIN,
            payload: { systemMode }
        });
        setSelectNewDomainTab(true);
    }, [systemMode]);

    const onDiscardClicked = useCallback(() => {
        if (newDomainIds.includes(selectedTab)) {
            dispatch({
                type: actions.DELETE_DOMAIN,
                payload: { domainId: selectedTab }
            });
        } else {
            discard(false);
        }
    }, [selectedTab, newDomainIds, discard]);

    const downloadCastleInstaller = useCallback(async () => {
        try {
            await httpPost('/Settings/generateCastleInstaller', { clientHostname: window.location.hostname });
            downloadFile('/Settings/downloadPreGeneratedCastleInstaller');
        } catch (err) {
            console.error('An error occurred while trying to download the AD Adapter Installer.');
        }
    }, []);

    const downloadAdConnectInstaller = useCallback(async () => {
        try {
            await httpPost('/Settings/generateAdConnectInstaller');
            downloadFile('/Settings/downloadPreGeneratedAdConnectInstaller');
        } catch (err) {
            console.error('An error occurred while trying to download the AD Connect Installer.');
        }
    }, []);

    const uploadFromFile = useCallback(
        (fileContent) => {
            const result = getNewDomainsFromFile(fileContent, settings.DomainsConfigurations);
            if (result.error) {
                return result.error;
            }
            for (const option of fields[LDAP_IP_OPTIONS.ID].value) {
                if (result.ldapServers.has(option.value)) {
                    result.ldapServers.delete(option.value);
                }
            }
            dispatch({
                type: actions.UPDATE_DOMAINS_FROM_FILE,
                payload: {
                    systemMode,
                    newDomains: result.newDomains,
                    LdapServerOptions: Array.from(result.ldapServers).map((option) => {
                        return { value: option, label: option };
                    })
                }
            });

            setShowUploadDomainsModal(false);

            return [];
        },
        [settings.DomainsConfigurations, fields]
    );
    const domainNodes = {};

    for (const nodeId in settings.NodesConfigurations) {
        if (settings.NodesConfigurations[nodeId].domainsConfiguration[selectedTab]) {
            domainNodes[nodeId] = settings.NodesConfigurations[nodeId];
        }
    }

    const addDomainButton = (
        <Button disabled={newDomainIds.length > 0 || searchQuery} onClick={addDomain}>
            + Add Domain
        </Button>
    );
    let content = null;
    if (visibleFields[selectedTab]) {
        content = (
            <ProtectedDomain
                domainId={selectedTab}
                fields={filterFieldsBySystemMode(visibleFields[selectedTab].fields, systemMode)}
                dispatch={dispatch}
                ldapServerOptions={fields[LDAP_IP_OPTIONS.ID].value}
                domainNodes={domainNodes}
                isNewDomain={newDomainIds.includes(selectedTab)}
                allowDelete={isEditor && !newDomainIds.includes(selectedTab)}
            />
        );
    }

    const bottomActions =
        systemMode !== SYSTEM_MODES.ON_PREM
            ? [
                  {
                      name: 'Download AD Adapter Installer',
                      clicked: _.debounce(() => {
                          downloadCastleInstaller();
                      }, 500)
                  },
                  {
                      name: 'Download AD Connect Installer',
                      clicked: _.debounce(() => {
                          downloadAdConnectInstaller();
                      }, 500)
                  }
              ]
            : [
                  {
                      name: 'Download AD Adapter Installer',
                      clicked: _.debounce(() => {
                          downloadCastleInstaller();
                      }, 500)
                  },
                  {
                      name: 'Upload from file',
                      clicked: () => {
                          setShowUploadDomainsModal(true);
                      }
                  }
              ];

    return (
        <>
            <Category
                category={PROTECTED_DOMAINS}
                fields={domainFields}
                visibleFields={visibleFields}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                saveSettings={save}
                discard={onDiscardClicked}
                discardAll={() => discard(true)}
                tabButton={addDomainButton}
                actions={bottomActions}
                disableSave={disableSave}>
                {content}
            </Category>

            {showUploadDomainsModal && (
                <ImportFromFile
                    cancelClicked={() => {
                        setShowUploadDomainsModal(false);
                    }}
                    importClicked={uploadFromFile}
                    modalTitle={'domains from file'}
                    importDescName={'domains'}
                    maxFileSize={10000000}
                    maxFileSizeText={'10 MB'}
                    importType={'JSON'}
                />
            )}
        </>
    );
};

export default ProtectedDomains;
